import React, { useEffect, useMemo, useState } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";

const fallbackCategoryItems = [];

const hasValidCount = (count) => {
  if (count === null || count === undefined) {
    return false;
  }

  const parsedCount = Number(count);
  return Number.isFinite(parsedCount) && parsedCount > 0;
};

const normalizeCategoryRows = (edges = []) =>
  edges
    .map((edge) => edge?.node)
    .filter(Boolean)
    .filter((node) => Boolean(node?.slug))
    .map((node) => ({
      name: node.name || "",
      slug: node.slug,
      count: node.count,
      parentSlug: node?.wpParent?.node?.slug || null,
    }));

const buildCategoryTree = (rows = []) => {
  const bySlug = new Map(rows.map((row) => [row.slug, row]));
  const includedSlugs = new Set();

  // Keep only categories with real product counts, plus all their ancestors to preserve hierarchy.
  rows.forEach((row) => {
    if (!hasValidCount(row.count)) {
      return;
    }

    let cursor = row;
    while (cursor && cursor.slug && !includedSlugs.has(cursor.slug)) {
      includedSlugs.add(cursor.slug);
      cursor = cursor.parentSlug ? bySlug.get(cursor.parentSlug) : null;
    }
  });

  const treeBySlug = new Map(
    rows
      .filter((row) => includedSlugs.has(row.slug))
      .map((row) => [
        row.slug,
        {
          name: row.name,
          slug: row.slug,
          hasProducts: hasValidCount(row.count),
          subcategories: [],
        },
      ])
  );

  const roots = [];
  rows
    .filter((row) => includedSlugs.has(row.slug))
    .forEach((row) => {
      const currentNode = treeBySlug.get(row.slug);
      if (!currentNode) {
        return;
      }

      const parentNode = row.parentSlug ? treeBySlug.get(row.parentSlug) : null;
      if (parentNode) {
        parentNode.subcategories.push(currentNode);
        return;
      }

      roots.push(currentNode);
    });

  const sortTree = (nodes) =>
    nodes
      .map((node) => ({
        ...node,
        subcategories: sortTree(node.subcategories || []),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

  return sortTree(roots);
};

export const useCategoryMenuItems = () => {
  const data = useStaticQuery(graphql`
    query CategoryMenuItemsQuery {
      allWpProductCategory {
        edges {
          node {
            name
            slug
            count
            wpParent {
              node {
                slug
              }
            }
          }
        }
      }
    }
  `);

  return useMemo(() => {
    const rows = normalizeCategoryRows(data?.allWpProductCategory?.edges || []);
    const tree = buildCategoryTree(rows);
    return tree.length > 0 ? tree : fallbackCategoryItems;
  }, [data]);
};

const toCategoryPath = (slug) => (slug ? `/shop-category/${slug}` : "#");

const normalizeCategoryNode = (node) => {
  if (typeof node === "string") {
    return { name: node, slug: "", hasProducts: false, children: [] };
  }

  if (!node || typeof node !== "object") {
    return { name: "", slug: "", hasProducts: false, children: [] };
  }

  const rawChildren = Array.isArray(node.subcategories)
    ? node.subcategories
    : Array.isArray(node.children)
      ? node.children
      : [];

  return {
    name: node.name || "",
    slug: node.slug || "",
    hasProducts: Boolean(node.hasProducts),
    children: rawChildren
      .map((childNode) => normalizeCategoryNode(childNode))
      .filter((childNode) => Boolean(childNode.name)),
  };
};

const CategoryMenuList = ({
  categories,
  variant = "accordion",
  allowMultipleOpen = false,
  activeCategory,
  onCategoryChange,
  className = "",
  itemClassName = "",
}) => {
  const queriedCategories = useCategoryMenuItems();
  const sourceCategories = Array.isArray(categories) ? categories : queriedCategories;
  const isAccordion = variant === "accordion";
  const isMega = variant === "mega";
  const normalizedCategories = useMemo(
    () =>
      sourceCategories
        .map((category) => {
          const normalizedCategory = normalizeCategoryNode(category);
          return {
            ...normalizedCategory,
            normalizedSubcategories: normalizedCategory.children,
          };
        })
        .filter((category) => Boolean(category.name)),
    [sourceCategories]
  );
  const [expandedCategoryNames, setExpandedCategoryNames] = useState(() =>
    normalizedCategories[0]?.name ? [normalizedCategories[0].name] : []
  );

  useEffect(() => {
    if (!normalizedCategories.length) {
      setExpandedCategoryNames([]);
      return;
    }

    setExpandedCategoryNames((previousNames) => {
      const validNames = previousNames.filter((name) =>
        normalizedCategories.some((category) => category.name === name)
      );

      if (validNames.length > 0) {
        return allowMultipleOpen ? validNames : [validNames[0]];
      }

      return [normalizedCategories[0].name];
    });
  }, [allowMultipleOpen, normalizedCategories]);

  const handleSelect = (category) => {
    if (onCategoryChange) {
      onCategoryChange(category);
    }
  };

  const handleAccordionToggle = (category) => {
    const isExpanded = expandedCategoryNames.includes(category.name);

    setExpandedCategoryNames((previousNames) => {
      if (allowMultipleOpen) {
        if (previousNames.includes(category.name)) {
          return previousNames.filter((name) => name !== category.name);
        }

        return [...previousNames, category.name];
      }

      return previousNames.includes(category.name) ? [] : [category.name];
    });

    if (onCategoryChange && !isExpanded) {
      onCategoryChange(category);
    }
  };

  const renderSubcategoryChildren = (children, parentKey, level = 1) =>
    children.map((childNode, index) => {
      const childKey = `${parentKey}-${childNode.name || `child-${index}`}-${index}`;
      const childClassName =
        "mt-[2px] text-[14px] leading-[24px] font-work-sans font-normal text-[#858585] hover:text-mqred7 transition-colors block";
      return (
        <div key={childKey}>
          {childNode.hasProducts && childNode.slug ? (
            <Link
              to={toCategoryPath(childNode.slug)}
              className={childClassName}
              style={{ paddingLeft: `${16 * level}px` }}
            >
              {childNode.name}
            </Link>
          ) : (
            <div className={childClassName} style={{ paddingLeft: `${16 * level}px` }}>
              {childNode.name}
            </div>
          )}
          {childNode.children.length > 0
            ? renderSubcategoryChildren(childNode.children, childKey, level + 1)
            : null}
        </div>
      );
    });

  if (isAccordion) {
    return (
      <div className={`space-y-[2px] ${className}`}>
        {normalizedCategories.map((category) => {
          const isExpanded = expandedCategoryNames.includes(category.name);

          return (
            <div key={category.name}>
              <div className={`w-full flex items-center justify-between gap-[8px] py-[8px] ${itemClassName}`}>
                {category.hasProducts && category.slug ? (
                  <Link
                    to={toCategoryPath(category.slug)}
                    className="text-_1624 font-work-sans font-semibold text-black hover:text-mqred7 transition-colors"
                    onClick={() => handleSelect(category)}
                  >
                    {category.name}
                  </Link>
                ) : (
                  <span className="text-_1624 font-work-sans font-semibold text-black">{category.name}</span>
                )}
                <button
                  type="button"
                  onClick={() => handleAccordionToggle(category)}
                  className="inline-flex items-center justify-center"
                  aria-expanded={isExpanded ? "true" : "false"}
                  aria-label={`${isExpanded ? "Collapse" : "Expand"} ${category.name}`}
                >
                  <svg
                    className={`w-[20px] h-[20px] stroke-current text-mqgrey4 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {isExpanded ? (
                <div className="pl-[16px] pb-[8px]">
                  {category.normalizedSubcategories.map((subcategory) => (
                    <div key={`${category.name}-${subcategory.name}`} className="mt-[8px]">
                      {subcategory.hasProducts && subcategory.slug ? (
                        <Link
                          to={toCategoryPath(subcategory.slug)}
                          className="text-left text-[14px] leading-[24px] font-work-sans font-medium text-black hover:text-mqred7 transition-colors"
                        >
                          {subcategory.name}
                        </Link>
                      ) : (
                        <div className="text-left text-[14px] leading-[24px] font-work-sans font-medium text-black">
                          {subcategory.name}
                        </div>
                      )}
                      {renderSubcategoryChildren(
                        subcategory.children,
                        `${category.name}-${subcategory.name}`,
                        1
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`${isMega ? "space-y-0" : "space-y-[12px]"} ${className}`}>
      {normalizedCategories.map((category) => {
        const isActive = activeCategory && activeCategory.name === category.name;
        const categoryClassName = `${isMega ? "" : "w-full"} text-left px-[12px] py-[10px] text-_1624 font-work-sans font-semibold transition-colors flex items-center justify-between group ${
          isMega
            ? `rounded-[6px] font-medium text-[14px] leading-[24px] px-[16px] py-[8px] ${
                isActive
                  ? "bg-white text-black border border-black"
                  : "text-black border border-transparent hover:bg-white"
              }`
            : "text-mqgrey5 hover:text-mqred7"
        } ${itemClassName}`;

        const iconNode = isMega ? (
          <svg
            className={`w-[24px] h-[24px] min-w-[24px] min-h-[24px] shrink-0 text-[#858585] transition-transform ${
              isActive ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <svg
            className="w-[20px] h-[20px] stroke-current group-hover:stroke-mqred7 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        );

        if (category.hasProducts && category.slug) {
          return (
            <Link
              key={category.name}
              to={toCategoryPath(category.slug)}
              onMouseEnter={() => handleSelect(category)}
              onFocus={() => handleSelect(category)}
              className={categoryClassName}
              aria-current={isActive ? "true" : "false"}
              data-mega-category-item={isMega ? "true" : undefined}
            >
              <span className={isMega ? "min-w-0 flex-1 pr-[8px] break-words" : ""}>{category.name}</span>
              {iconNode}
            </Link>
          );
        }

        return (
          <button
            key={category.name}
            type="button"
            onClick={() => handleSelect(category)}
            onMouseEnter={() => handleSelect(category)}
            className={categoryClassName}
            aria-current={isActive ? "true" : "false"}
            data-mega-category-item={isMega ? "true" : undefined}
          >
            <span className={isMega ? "min-w-0 flex-1 pr-[8px] break-words" : ""}>{category.name}</span>
            {iconNode}
          </button>
        );
      })}
    </div>
  );
};

export default CategoryMenuList;

